import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, List, ListItemButton, ListItemText,
  Popover, Radio, RadioGroup, Checkbox, Typography
} from "@mui/material";
import { Close, Check, ChevronRight, SearchOutlined, TuneOutlined } from "@mui/icons-material";
import React, { useState } from "react";
import { tokens, mono } from "../../../theme";
import { tints } from "../../../theme/overrides";
import type { RecentContext, ViewAsOption } from "./contextSwitcherModel";

interface ContextSwitcherProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  recents: RecentContext[];
  onPickRecent: (ctx: RecentContext) => void;
  onOpenChooser: () => void;
  /** Row hidden when the user's nav has no System Management entry. */
  onOpenSystemManagement?: () => void;
}

/**
 * Replaces "Portal Management" + AccountSelectionView.
 *
 * Two behaviours the old flow did not have:
 *  1. It is a Popover over the LIVE dashboard, not a screen that destroys it,
 *     so Cancel always works.
 *  2. Recent contexts are one click; the full chooser is the fallback, not the
 *     default. Most users re-open the same two or three.
 */
export const ContextPopover = (props: ContextSwitcherProps) => {
  const { anchorEl, onClose, recents, onPickRecent, onOpenChooser, onOpenSystemManagement } = props;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ sx: { width: 372, boxShadow: tokens.shadow.popover, mt: 1 } }}
    >
      <Typography sx={{ p: "12px 16px 8px", fontFamily: mono, fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: tokens.color.faint }}>
        Recent
      </Typography>
      <List disablePadding>
        {recents.map((r) => (
          <ListItemButton key={r.id} onClick={() => onPickRecent(r)} sx={{ px: 2, py: 1.25, gap: 1.5 }}>
            <Avatar variant="rounded" sx={{ width: 30, height: 30, bgcolor: r.tint, color: r.fg, fontSize: 12, fontWeight: 600 }}>
              {r.initials}
            </Avatar>
            <ListItemText
              primary={r.client}
              secondary={r.detail}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: tokens.color.ink, noWrap: true }}
              secondaryTypographyProps={{ fontSize: 12, color: tokens.color.muted, noWrap: true }}
            />
            <Chip size="small" label={r.program} sx={{ bgcolor: r.tint, color: r.fg, flex: "none" }} />
            {r.active && <Check sx={{ fontSize: 16, color: tokens.color.positive.main, ml: 1 }} />}
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ my: 0.75 }} />
      <ListItemButton onClick={onOpenChooser} sx={{ px: 2, py: 1.4, gap: 1.25 }}>
        <SearchOutlined sx={{ fontSize: 17, color: tokens.color.primary }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.color.primary }}>
          {/* No count: the true total is clients × their programs, which is not
              known until every client's programs have been fetched. */}
          Browse all clients…
        </Typography>
      </ListItemButton>
      {onOpenSystemManagement && (
        <>
          <Divider />
          <ListItemButton onClick={onOpenSystemManagement} sx={{ px: 2, py: 1.4, gap: 1.25 }}>
            <TuneOutlined sx={{ fontSize: 17, color: tokens.color.body }} />
            <Typography sx={{ fontSize: 13, color: tokens.color.body }}>System Management</Typography>
          </ListItemButton>
        </>
      )}
    </Popover>
  );
};

interface ChooserProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (makeDefault: boolean) => void;
  clientLabel?: string;
  programLabel?: string;
  viewAsOptions: ViewAsOption[];
  viewAs?: string;
  onViewAsChange: (id: string) => void;
  subScope?: React.ReactNode;
  /** Step 1 and 2 pickers. Each shows only while its answer is missing. */
  clientPicker?: React.ReactNode;
  programPicker?: React.ReactNode;
  /** Hidden for client users, who are pinned to one client. */
  showClientStep?: boolean;
  /**
   * "Make this my default" is offered only where a default can later be
   * applied — client users. An admin's saved default could never be rebuilt
   * from the store on landing (see portalShell), so the checkbox would be a
   * dead control for them.
   */
  showMakeDefault?: boolean;
  busy?: boolean;
  /** Step chips are go-back controls: each clears its answer and everything
   *  downstream of it, which returns the flow to that step. */
  onEditClient?: () => void;
  onEditProgram?: () => void;
}

/**
 * Three steps, and the answers so far stay on screen as clickable chips.
 * Continue is disabled until the CURRENT step is valid, so the five-errors-at-once
 * dump from accountSelectionView.validate() is structurally impossible.
 */
export const ContextChooser = (props: ChooserProps) => {
  const {
    open, onClose, onConfirm, clientLabel, programLabel,
    viewAsOptions, viewAs, onViewAsChange, subScope,
    clientPicker, programPicker, showClientStep = true, showMakeDefault = true, busy,
    onEditClient, onEditProgram
  } = props;
  const [makeDefault, setMakeDefault] = useState(false);
  const clientDone = !showClientStep || Boolean(clientLabel);
  const canConfirm = clientDone && Boolean(programLabel) && (viewAsOptions.length === 0 || Boolean(viewAs));

  // One step at a time, so an unanswered step cannot be skipped — this is what
  // makes the old five-errors-at-once validate() impossible.
  const step = !clientDone ? 1 : !programLabel ? 2 : 3;

  const stepChip = (
    caption: string,
    index: number,
    value?: string,
    active?: boolean,
    onEdit?: () => void
  ) => (
    <Box
      component={onEdit ? "button" : "div"}
      type={onEdit ? "button" : undefined}
      onClick={onEdit}
      disabled={onEdit ? busy : undefined}
      aria-label={onEdit ? `Change ${caption.toLowerCase()}` : undefined}
      sx={{
        font: "inherit",
        display: "flex", alignItems: "center", gap: 1.1, px: 1.5, py: 0.9,
        border: active ? `1.5px solid ${tokens.color.primary}` : `1px solid ${tokens.color.borderHover}`,
        bgcolor: active ? tints.primaryChip : "transparent",
        borderRadius: `${tokens.radius.control}px`,
        cursor: onEdit ? "pointer" : "default",
        transition: `border-color ${tokens.motion.base}`,
        "&:hover": onEdit ? { borderColor: tokens.color.primary } : undefined
      }}
    >
      {value ? (
        <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: tokens.color.positive.main, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Check sx={{ fontSize: 11, color: "#fff" }} />
        </Box>
      ) : (
        <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: tokens.color.primary, color: "#fff", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {index}
        </Box>
      )}
      <Typography sx={{ fontSize: 12, color: tokens.color.muted }}>{caption}</Typography>
      {value && <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.color.ink }}>{value}</Typography>}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: `${tokens.radius.card + 2}px`, boxShadow: tokens.shadow.modal } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: tokens.color.ink }}>Choose a dashboard</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.color.muted }}>Three steps. You can go back to any of them.</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close sx={{ fontSize: 17 }} /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          {showClientStep && (
            <>
              {stepChip("Client", 1, clientLabel, step === 1, clientLabel ? onEditClient : undefined)}
              <ChevronRight sx={{ fontSize: 14, color: tokens.color.borderHover }} />
            </>
          )}
          {stepChip("Program", 2, programLabel, step === 2, programLabel ? onEditProgram : undefined)}
          <ChevronRight sx={{ fontSize: 14, color: tokens.color.borderHover }} />
          {stepChip("View as", 3, viewAs ? " " : undefined, step === 3)}
        </Box>

        {step === 1 && clientPicker}
        {step === 2 && programPicker}

        {step === 3 && (
        <RadioGroup value={viewAs ?? ""} onChange={(e) => onViewAsChange(e.target.value)} sx={{ gap: 1.1 }}>
          {viewAsOptions.map((o) => (
            <Box key={o.id}
              sx={{
                display: "flex", alignItems: "center", gap: 1.75, p: "14px 16px",
                border: viewAs === o.id ? `1.5px solid ${tokens.color.primary}` : `1px solid ${tokens.color.border}`,
                bgcolor: viewAs === o.id ? tints.primaryPanel : tokens.color.surface,
                borderRadius: `${tokens.radius.card - 2}px`,
                transition: `border-color ${tokens.motion.base}, background ${tokens.motion.base}`
              }}>
              <Radio value={o.id} size="small" sx={{ p: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.color.ink }}>{o.title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.color.muted, lineHeight: 1.45 }}>{o.body}</Typography>
              </Box>
              <Typography sx={{ fontFamily: mono, fontSize: 11, color: tokens.color.faint }}>{o.code}</Typography>
            </Box>
          ))}
        </RadioGroup>
        )}

        {step === 3 && subScope}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: showMakeDefault ? "space-between" : "flex-end", px: 3, py: 2 }}>
        {showMakeDefault && (
          <FormControlLabel
            control={<Checkbox size="small" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: tokens.color.body }}>Make this my default</Typography>}
          />
        )}
        <Box sx={{ display: "flex", gap: 1.1 }}>
          <Button variant="text" onClick={onClose} sx={{ color: tokens.color.body }}>Cancel</Button>
          <Button variant="contained" disabled={!canConfirm || busy} onClick={() => onConfirm(makeDefault)}>
            Open dashboard
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
